import { isCollectible, ROOF_HEIGHT } from "../data/entities";
import type { WorldEntity } from "../data/model";
import { motor as M } from "../data/tuning";
import type { RunSession } from "./RunSession";

const LANE = 4;
const SURFACES = new Set(["truck", "mover", "ramp", "platform", "deck"]);

/**
 * A competent driver for the demo and the death audit. It reads the road the way a player does: time to contact
 * (oncoming trucks close at their own speed plus yours), hops what is low, ducks what is high, hops the gap between
 * two roofs, and changes lanes away from walls. A death this driver cannot avoid is a road worth looking at.
 */
export class Autopilot {
  private cooldown = 0;
  /** dev: receives each decision */
  trace?: (line: string) => void;

  step(s: RunSession, dt: number): void {
    this.cooldown = Math.max(0, this.cooldown - dt);
    const m = s.motor;
    if (m.state === "flying") return;
    const z = m.z, y = m.y, lane = m.lane, v = Math.max(1, m.speed);
    const onRoof = y > ROOF_HEIGHT - 1;
    const active = s.entities.filter((e) => e.active && !isCollectible(e.kind) && e.kind !== "gate");
    const inLane = (e: WorldEntity, l: number) => Math.abs(e.x - l * LANE) < e.width / 2 + M.colliderHalfWidth - 0.05;
    const closing = (e: WorldEntity) => v * (1 + (e.kind === "mover" ? e.speedFactor ?? 0 : 0));
    /** seconds until its front reaches the car's nose, and until its back passes the car's tail */
    const contact = (e: WorldEntity) => (e.z - z - 0.6) / closing(e);
    const clears = (e: WorldEntity) => (e.z + e.depth - z + 0.6) / closing(e);
    const carTop = (h: number) => h + M.colliderHeight;
    /** something in lane l that would hit a car at height h: not a surface you ride, not overhead */
    const threat = (e: WorldEntity, l: number, h: number) => {
      if (!inLane(e, l)) return false;
      if (e.kind === "deck") return false;
      if (e.yBase >= carTop(h) - 0.05) return false;                         // passes over you
      const top = e.yBase + e.height;
      if (SURFACES.has(e.kind) && h >= top - 0.6) return false;               // you ride it
      if (e.kind === "ramp") return false;                                    // a ramp lifts you
      return true;
    };
    const threatsIn = (l: number, h: number, from: number, to: number) => active
      .filter((e) => threat(e, l, h))
      .map((e) => ({ e, t: contact(e), end: clears(e) }))
      .filter((o) => o.end > from && o.t < to)
      .sort((a, b) => a.t - b.t);
    /** hop it (its top within a 4 m hop) or duck it (it starts above a rolled car) */
    const passable = (e: WorldEntity, h: number) => e.kind !== "mover"
      && (e.yBase + e.height <= h + 1.95 || e.yBase >= h + M.colliderHeight * M.rollHeightFactor + 0.02);
    /** a lane you can move into: no wall or truck front for 1.3 s, and nothing at all in the next 0.3 s */
    const free = (l: number, h: number) => l >= -1 && l <= 1
      && threatsIn(l, h, 0, 1.3).every((o) => passable(o.e, h)) && threatsIn(l, h, 0, 0.25).length === 0;

    // 1. a roof that ends: hop the gap when another roof follows, otherwise drop and look again on the road
    if (onRoof && !m.airborne) {
      const under = active.find((e) => SURFACES.has(e.kind) && e.kind !== "deck" && inLane(e, lane) && e.z <= z + 0.6 && e.z + e.depth > z - 0.6);
      if (under) {
        const end = under.z + under.depth;
        const next = active.find((e) => (e.kind === "truck" || e.kind === "ramp" || e.kind === "mover") && inLane(e, lane) && e.z >= end - 0.1 && e.z < end + v * 0.8);
        if (next && end - z < v * 0.12) { s.action("jump"); return; }
      }
    }

    // 2. ramps: take one in a neighbouring lane when the way there is clear (roofs are safer)
    if (!onRoof && !m.airborne && this.cooldown <= 0) {
      for (const dl of [0, 1, -1]) {
        const l = lane + dl;
        if (l < -1 || l > 1) continue;
        const ramp = active.find((e) => e.kind === "ramp" && inLane(e, l) && contact(e) > 0.25 && contact(e) < 1.2);
        if (ramp && free(l, 0) && threatsIn(l, 0, -0.2, contact(ramp)).length === 0) {
          if (dl) { s.action(dl > 0 ? "right" : "left"); this.cooldown = 0.35; }
          return;
        }
      }
    }

    // 3. the first thing in the way in this lane
    const first = threatsIn(lane, y, -0.05, 1.4)[0];
    if (!first) return;
    const { e, t } = first;
    const top = e.yBase + e.height;
    const hopClear = top <= y + 1.95 && e.kind !== "mover";                   // a 4 m hop clears 1.93 m
    const duckClear = passable(e, y) && !hopClear;
    if (hopClear && !m.airborne) { if (t < 0.42) s.action("jump"); return; }
    if (duckClear) { if (t < 0.32) s.action("roll"); return; }
    this.trace?.(`z${z.toFixed(1)} lane${lane} first ${e.kind}@${e.z.toFixed(1)} t${t.toFixed(2)} cd${this.cooldown.toFixed(2)} free(-1)${free(-1, y)} free(0)${free(0, y)} free(1)${free(1, y)} | lane0 ${threatsIn(0, y, 0, 1.3).map((o) => o.e.kind + "@" + o.e.z.toFixed(0) + " t" + o.t.toFixed(2) + " end" + o.end.toFixed(2)).join(",")}`);
    if (this.cooldown > 0) return;
    // a wall, a truck front, an oncoming truck: the free lane with the most room — judged at the height the car will
    // have when it gets there (mid-hop it is coming down: a roof beside it is a wall, not a landing)
    const hLane = m.airborne ? Math.max(0, y + Math.min(0, m.vy) * 0.3 - 1.8) : y;
    let best = 0, room = -Infinity;
    for (const dl of [1, -1]) {
      const l = lane + dl;
      if (!free(l, hLane)) continue;
      const nextIn = threatsIn(l, hLane, 0, 4).find((o) => !passable(o.e, hLane))?.t ?? 4;
      if (nextIn > room) { room = nextIn; best = dl; }
    }
    if (best) { s.action(best > 0 ? "right" : "left"); this.cooldown = 0.3; return; }
    // boxed in: two lanes away may be free if the middle is passable now
    // (a change retargets from where the car is: it is over the middle lane from ~0.08 s to ~0.27 s into the double change)
    if (lane !== 0 && free(-lane, hLane) && threatsIn(0, hLane, 0, 0.3).every((o) => passable(o.e, hLane))) {
      s.action(lane > 0 ? "left" : "right");
      this.cooldown = 0.05;                                                  // the next step sees the far lane free and goes on
    }
  }
}
